import { clearSharedData, ClearSharedDataRequestModel } from "client/Api/InstanceOperatorPage/instanceOperatorPage";
import {
	BusyIndicator,
	Checkbox,
	Form,
	FormItem,
	Toolbar
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { AlertManager, useAlerts } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { navigateTo } from "client_legacy/ndaw/navigator";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { createRoot } from "react-dom/client";
import { ContentBlock } from "../PublicPage/ContentBlock";
import { InstanceOperatorLayout } from "./InstanceOperatorLayout";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { ensureNotNil } from "client/Misc/utils";

export const ClearSharedData = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<ClearSharedDataRequestModel>({
		cache: true,
		temporaryData: false
	});

	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<ClearSharedDataRequestModel> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				await clearSharedData(formData);

				navigateTo("/admin");
			} catch (e) {
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			isBusy
		]
	);

	return (
		<InstanceOperatorLayout>
			<ContentBlock size="small">
				{isBusy && (
					<BusyIndicator />
				)}

				<h1>Which shared data do you want to clear on ALL environments?</h1>

				<Form onSubmit={submitHandler}>
					<FormItem>
						<NoUniqueInputIdContext>
							<Checkbox
								{...formHandler.for("cache").attachToCheckbox()}
								text="Cache"
							/>

							<Checkbox
								{...formHandler.for("temporaryData").attachToCheckbox()}
								text="Temporary data - This will DELETE the temporary data permanently"
								icon={{
									kind: "font",
									name: "triangle-exclamation",
									color: "danger"
								}}
							/>
						</NoUniqueInputIdContext>
					</FormItem>

					<FormItem>
						<Toolbar>
							<Toolbar.Button
								onClick={submitHandler}
								kind="secondary"
								text="Clear"
							/>
						</Toolbar>
					</FormItem>
				</Form>
			</ContentBlock>
		</InstanceOperatorLayout>
	);
};
ClearSharedData.displayName = "ClearSharedData";

export function init(): void {
	createRoot(
		ensureNotNil(
			document.getElementById("instance-operator-clear-shared-data")
		)
	)
		.render(
			<ClearSharedData />
		);
}
